import { ComponentType } from '@angular/cdk/portal';
import { Component } from '@angular/core';

import { DemoAngularButtonComponent } from '@demo/angular/button/demo-angular-button.component';
import { DemoThemeComponent } from '@demo/angular/demo-theme/demo-theme.component';
import { DialogsComponent } from '@demo/angular/dialogs/dialogs.component';
import { DemoAngularInputComponent } from '@demo/angular/input/demo-angular-input.component';
import { DemoTypographyComponent } from '@demo/angular/typography/typography.component';
import { DemoAddressAutocompleteComponent } from '@demo/demo-cipo-fields/demo-address-autocomplete/demo-address-autocomplete.component';
import { DemoCipoAttachmentComponent } from '@demo/demo-cipo-fields/demo-cipo-attachment/demo-cipo-attachment.component';
import { DemoCipoAutocompleteComponent } from '@demo/demo-cipo-fields/demo-cipo-autocomplete/demo-cipo-autocomplete.component';
import { DemoCipoCheckboxComponent } from '@demo/demo-cipo-fields/demo-cipo-checkbox/demo-cipo-checkbox.component';
import { DemoCipoDateTimeComponent } from '@demo/demo-cipo-fields/demo-cipo-date-time/demo-cipo-date-time.component';
import { DemoCipoFieldsComponent } from '@demo/demo-cipo-fields/demo-cipo-fields.component';
import { DemoCipoListboxComponent } from '@demo/demo-cipo-fields/demo-cipo-listbox/demo-cipo-listbox.component';
import { DemoCipoNumberComponent } from '@demo/demo-cipo-fields/demo-cipo-number/demo-cipo-number.component';
import { DemoCipoRadioGroupComponent } from '@demo/demo-cipo-fields/demo-cipo-radio-group/demo-cipo-radio-group.component';
import { DemoCipoSelectComponent } from '@demo/demo-cipo-fields/demo-cipo-select/demo-cipo-select.component';
import { DemoCipoTextComponent } from '@demo/demo-cipo-fields/demo-cipo-text/demo-cipo-text.component';
import { DemoReadonlyFieldsComponent } from '@demo/demo-cipo-fields/demo-readonly-fields/demo-readonly-fields.component';
import { DemoTableFieldComponent } from '@demo/demo-cipo-fields/demo-table-field/demo-table-field.component';
import { DemoFroalaComponent } from '@demo/demo-froala/demo-froala.component';
import { HelperClassesComponent } from '@demo/helper-classes/helper-classes.component';
import { DemoComplexConditionsComponent } from '@demo/module-definition/demo-complex-conditions/demo-complex-conditions.component';
import { DemoDataCardSettingsComponent } from '@demo/module-definition/demo-data-card-settings/demo-data-card-settings.component';
import { DemoFormBuilderComponent } from '@demo/module-definition/demo-form-builder/demo-form-builder.component';
import { DemoFormulasComponent } from '@demo/module-definition/demo-formulas/demo-formulas.component';
import { DemoCalendarViewComponent } from '@demo/module-instance/calendar-view/demo-calendar-view.component';
import { DemoCardsViewComponent } from '@demo/module-instance/cards-view/demo-cards-view.component';
import { DemoScheduledEmailsComponent } from '@demo/module-instance/demo-scheduled-emails/demo-scheduled-emails.component';
import { TableComponent } from '@demo/module-instance/table/table.component';
import { DemoContextComponent } from '@demo/system/context/demo-context.component';
import { MenuItem } from '@models/menu';
import { DataListType } from '@models/module/fields/main';
import { DemoFilesTreeComponent } from './module-definition/demo-files-tree/demo-files-tree.component';

export interface DemoMenuItem extends MenuItem {
  component?: ComponentType<any>;
  loadComponent?: () => Promise<ComponentType<any>>;
  children?: DemoMenuItem[];
}

@Component({
  selector: 'app-demo-empty',
  template: '',
})
export class DemoEmptyComponent {}

export const demoRoutesUrlDict = {
  1: 'helper-classes',
  101: 'buttons',
  102: 'inputs',
  103: 'theme',
  104: 'froala',
  105: 'address',
  106: 'typography',
  107: 'dialogs',
  151: 'custom-form',
  152: 'cipo-text',
  153: 'cipo-number',
  154: 'cipo-listbox',
  155: 'cipo-checkbox',
  156: 'cipo-select',
  157: 'cipo-attachment',
  158: 'cipo-datetime',
  159: 'cipo-table-field',
  161: 'cipo-readonly-fields',
  162: 'cipo-autocomplete',
  163: 'cipo-radio-group',
  201: 'card-settings',
  203: 'form-builder',
  205: 'complex-conditions',
  207: 'formulas',
  208: 'address',
  301: 'calendar-view',
  302: 'cards-view',
  303: 'example-table',
  304: 'scheduled-emails',
  305: 'tenant-settings',
  306: 'files-tree',
  401: 'context',
  402: 'search',
  1001: 'test-2nd-level-m1',
  1002: 'test-2nd-level-m2',
  1101: 'test-3nd-level-m3-1',
  1102: 'test-3nd-level-m3-2',
  1201: 'test-3nd-level-m4-1',
  1202: 'test-3nd-level-m4-2',
} as const;

export const DemoMenuItems: DemoMenuItem[] = [
  {
    id: 1,
    name: 'Helper classes',
    component: HelperClassesComponent,
    urlPath: demoRoutesUrlDict[1],
    defaultDataListType: DataListType.Module,
  },
  {
    id: 100,
    name: 'Angular',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 101,
        name: 'Button',
        iconId: 40,
        urlPath: demoRoutesUrlDict[101],
        component: DemoAngularButtonComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 102,
        name: 'Input',
        iconId: 21,
        urlPath: demoRoutesUrlDict[102],
        component: DemoAngularInputComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 103,
        name: 'Theme',
        iconId: 24,
        urlPath: demoRoutesUrlDict[103],
        component: DemoThemeComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 104,
        name: 'Froala',
        iconId: 46,
        urlPath: demoRoutesUrlDict[104],
        component: DemoFroalaComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 106,
        name: 'Typography',
        iconId: 20,
        urlPath: demoRoutesUrlDict[106],
        component: DemoTypographyComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 107,
        name: 'Dialogs',
        iconId: 22,
        urlPath: demoRoutesUrlDict[107],
        component: DialogsComponent,
        defaultDataListType: DataListType.Module,
      },
    ],
  },
  {
    id: 150,
    name: 'Fields',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 151,
        name: 'Custom form',
        iconId: 22,
        urlPath: demoRoutesUrlDict[151],
        component: DemoCipoFieldsComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 152,
        name: 'Cipo text',
        iconId: 23,
        urlPath: demoRoutesUrlDict[152],
        component: DemoCipoTextComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 153,
        name: 'Cipo number',
        iconId: 24,
        urlPath: demoRoutesUrlDict[153],
        component: DemoCipoNumberComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 154,
        name: 'Cipo listbox',
        iconId: 25,
        urlPath: demoRoutesUrlDict[154],
        component: DemoCipoListboxComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 163,
        name: 'Cipo Radio Group',
        iconId: 25,
        urlPath: demoRoutesUrlDict[163],
        component: DemoCipoRadioGroupComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 155,
        name: 'Cipo checkbox',
        iconId: 26,
        urlPath: demoRoutesUrlDict[155],
        component: DemoCipoCheckboxComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 157,
        name: 'Cipo attachment',
        iconId: 28,
        urlPath: demoRoutesUrlDict[157],
        component: DemoCipoAttachmentComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 156,
        name: 'Cipo select',
        iconId: 27,
        urlPath: demoRoutesUrlDict[156],
        component: DemoCipoSelectComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 158,
        name: 'Cipo DateTime',
        iconId: 28,
        urlPath: demoRoutesUrlDict[158],
        component: DemoCipoDateTimeComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 159,
        name: 'Cipo Table field',
        iconId: 29,
        urlPath: demoRoutesUrlDict[159],
        component: DemoTableFieldComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 161,
        name: 'Cipo Readonly Fields',
        iconId: 21,
        urlPath: demoRoutesUrlDict[161],
        component: DemoReadonlyFieldsComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 162,
        name: 'Cipo Autocomplete',
        iconId: 30,
        urlPath: demoRoutesUrlDict[162],
        component: DemoCipoAutocompleteComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 163,
        name: 'Cipo Radio Group',
        iconId: 25,
        urlPath: demoRoutesUrlDict[163],
        component: DemoCipoRadioGroupComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 207,
        name: 'Cipo Formula',
        iconId: 92,
        urlPath: demoRoutesUrlDict[207],
        component: DemoFormulasComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 208,
        name: 'Cipo Address',
        iconId: 21,
        urlPath: demoRoutesUrlDict[208],
        component: DemoAddressAutocompleteComponent,
        defaultDataListType: DataListType.Module,
      },
    ],
  },
  {
    id: 200,
    name: 'Module Definition',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 201,
        name: 'Card settings',
        iconId: 65,
        urlPath: demoRoutesUrlDict[201],
        component: DemoDataCardSettingsComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 203,
        name: 'Form Builder',
        iconId: 13,
        urlPath: demoRoutesUrlDict[203],
        component: DemoFormBuilderComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 205,
        name: 'Complex Conditions',
        iconId: 90,
        urlPath: demoRoutesUrlDict[205],
        component: DemoComplexConditionsComponent,
        defaultDataListType: DataListType.Module,
      },
    ],
  },
  {
    id: 300,
    name: 'Module Instance',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 301,
        name: 'Calendar view',
        iconId: 95,
        urlPath: demoRoutesUrlDict[301],
        component: DemoCalendarViewComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 302,
        name: 'Cards view',
        iconId: 13,
        urlPath: demoRoutesUrlDict[302],
        component: DemoCardsViewComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 303,
        name: 'Example table',
        iconId: 13,
        urlPath: demoRoutesUrlDict[303],
        component: TableComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 304,
        name: 'Scheduled emails',
        iconId: 66,
        urlPath: demoRoutesUrlDict[304],
        component: DemoScheduledEmailsComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 305,
        name: 'Tenant Settings',
        iconId: 67,
        urlPath: demoRoutesUrlDict[305],
        loadComponent: () =>
          import('@demo/module-instance/demo-tenant-settings/demo-tenant-settings.component').then(
            m => m.DemoTenantSettingsComponent,
          ),
        defaultDataListType: DataListType.Module,
      },
      {
        id: 306,
        name: 'Files Tree',
        iconId: 68,
        urlPath: demoRoutesUrlDict[306],
        component: DemoFilesTreeComponent,
        defaultDataListType: DataListType.Module,
      },
    ],
  },
  {
    id: 400,
    name: 'System',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 401,
        name: 'Context',
        iconId: 73,
        urlPath: demoRoutesUrlDict[401],
        component: DemoContextComponent,
        defaultDataListType: DataListType.Module,
      },
    ],
  },
  {
    id: 1000,
    name: 'Test 1st level',
    defaultDataListType: DataListType.Module,
    children: [
      {
        id: 1001,
        name: 'Test 2nd level m1',
        urlPath: demoRoutesUrlDict[1001],
        component: DemoEmptyComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 1002,
        name: 'Test 2nd level m2',
        urlPath: demoRoutesUrlDict[1002],
        component: DemoEmptyComponent,
        defaultDataListType: DataListType.Module,
      },
      {
        id: 1003,
        name: 'Test 2nd level m3',
        defaultDataListType: DataListType.Module,
        children: [
          {
            id: 1101,
            name: 'Test 3nd level m3.1',
            urlPath: demoRoutesUrlDict[1101],
            component: DemoEmptyComponent,
            defaultDataListType: DataListType.Module,
          },
          {
            id: 1102,
            name: 'Test 3nd level m3.2',
            urlPath: demoRoutesUrlDict[1102],
            component: DemoEmptyComponent,
            defaultDataListType: DataListType.Module,
          },
        ],
      },
      {
        id: 1004,
        name: 'Test 2nd level m4',
        defaultDataListType: DataListType.Module,
        children: [
          {
            id: 1201,
            name: 'Test 3nd level m4.1',
            urlPath: demoRoutesUrlDict[1201],
            component: DemoEmptyComponent,
            defaultDataListType: DataListType.Module,
          },
          {
            id: 1202,
            name: 'Test 3nd level m4.2',
            urlPath: demoRoutesUrlDict[1202],
            component: DemoEmptyComponent,
            defaultDataListType: DataListType.Module,
          },
        ],
      },
    ],
  },
];

export const mapMenuItems = (tenantUrl: string) => {
  const mapMenuItems = (items: DemoMenuItem[]): any[] =>
    items.map(({ component, urlPath, children, ...rest }) => ({
      ...rest,
      urlPath: urlPath ? `${tenantUrl}/demo/${urlPath}` : undefined,
      children: children ? mapMenuItems(children) : undefined,
    }));

  return mapMenuItems(DemoMenuItems);
};

function flattenMenu(items: DemoMenuItem[]): DemoMenuItem[] {
  return items.reduce<DemoMenuItem[]>((acc, item) => {
    if (item.children) {
      acc.push(...flattenMenu(item.children));
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
}

export const flatMenuItems = flattenMenu(DemoMenuItems);
